import * as React from 'react';
import moment from 'moment';
import { Redirect, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Alert, Card, CardBody, FormGroup, Input, Label } from 'reactstrap';
import TaskCompleteDialog from '../../components/Tasks/TaskCompleteDialog';
import TaskCreateModal from '../../components/Tasks/TaskCreateModal';
import TaskItemsTable, { TaskRowEditState } from '../../components/Tasks/TaskItemsTable';
import {
    AssignTaskItemData,
    TaskItemData,
    TaskItemStatus,
    UpdateTaskItemData,
    UserData,
} from '../../models';
import {
    assignTaskAsync,
    getTaskAssignableUsersAsync,
    getTasksAsync,
    GetTasksParams,
    updateTaskAsync,
    updateTaskStatusAsync,
} from '../../ajax/requests';
import { getEntitiesDataItems } from '../../store/entities/selectors';
import { ClientEntity } from '../../store/entities/state';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { getMyUser } from '../../store/session/selectors';
import {
    canCreateTasks,
    getTaskDismissReasonOptions,
    hasExtendedTaskManagementScope,
    sortTasksByDueDate,
    TaskDismissReasons,
} from './taskUtils';
import { paths } from '../../app/constants';
import { getTasksUpdatedAt } from '../../store/general/selectors';
import { getSettings } from '../../store/settings/selectors';
import AppButton from '../../components/AppButton';

interface TaskFiltersState {
    status?: TaskItemStatus;
    assignedUserId?: string;
    dateFrom?: string;
    dateTo?: string;
}

const TasksPage: React.FC = () => {
    const t = useTranslations();
    const history = useHistory();
    const user = useSelector(getMyUser);
    const settings = useSelector(getSettings);
    const tasksUpdatedAt = useSelector(getTasksUpdatedAt);
    const dismissReasonOptions = React.useMemo(() => getTaskDismissReasonOptions(), []);

    const clients = useSelector(getEntitiesDataItems<ClientEntity>('clients'));
    const [tasks, setTasks] = React.useState<TaskItemData[]>([]);
    const [users, setUsers] = React.useState<UserData[]>([]);
    const [isLoading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const [filters, setFilters] = React.useState<TaskFiltersState>({
        status: TaskItemStatus.Pending,
        dateFrom: moment().subtract(2, 'months').format('YYYY-MM-DD'),
        dateTo: moment().add(1, 'month').format('YYYY-MM-DD'),
    });

    const [completeTask, setCompleteTask] = React.useState<TaskItemData | null>(null);
    const [dismissTask, setDismissTask] = React.useState<TaskItemData | null>(null);
    const [dismissReason, setDismissReason] = React.useState<string>(TaskDismissReasons.notApplicable);
    const [dismissOtherReason, setDismissOtherReason] = React.useState('');
    const [dismissNotes, setDismissNotes] = React.useState('');
    const [rowEdit, setRowEdit] = React.useState<TaskRowEditState | null>(null);
    const [isSavingRow, setSavingRow] = React.useState(false);
    const [showCreate, setShowCreate] = React.useState(false);
    const editingRowRef = React.useRef<HTMLTableRowElement | null>(null);

    const canCreateTaskItems = canCreateTasks(user);
    const hasScopedManagementAccess = React.useMemo(() => hasExtendedTaskManagementScope(user, users), [user, users]);
    const isTaskManagementEnabled = Boolean(settings.taskManagement?.enabled);

    const loadTasks = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params: GetTasksParams = {
                ...filters,
                limit: 300,
                offset: 0,
            };
            const result = await getTasksAsync(params);
            setTasks(sortTasksByDueDate(result));
        } catch {
            setTasks([]);
            setError(t(TK.somethingWentWrong));
        } finally {
            setLoading(false);
        }
    }, [filters, t]);

    React.useEffect(() => {
        loadTasks();
    }, [loadTasks, tasksUpdatedAt]);

    React.useEffect(() => {
        if (!canCreateTaskItems) {
            setUsers([]);
            return;
        }

        getTaskAssignableUsersAsync()
            .then(scopeUsers => {
                if (scopeUsers.length > 0) {
                    setUsers(scopeUsers);
                    return;
                }

                setUsers(user ? [user] : []);
            })
            .catch(() => setUsers(user ? [user] : []));
    }, [canCreateTaskItems, user]);

    if (!isTaskManagementEnabled) {
        return <Redirect to={paths.root()} />;
    }

    const navigateToClient = (clientId?: string, tab?: string) => {
        if (!clientId) return;
        history.push({
            pathname: paths.clients(),
            search: tab ? `?id=${clientId}&tab=${encodeURIComponent(tab)}` : `?id=${clientId}`,
        });
    };

    const navigateToProfessional = (professionalId?: string, tab?: string) => {
        if (!professionalId) return;
        history.push({
            pathname: paths.professionals(),
            search: tab ? `?id=${professionalId}&tab=${encodeURIComponent(tab)}` : `?id=${professionalId}`,
        });
    };

    const handleDismissTask = async (task: TaskItemData) => {
        const reason = dismissReason === TaskDismissReasons.other ? dismissOtherReason : dismissReason;
        try {
            await updateTaskStatusAsync(task.id, {
                status: TaskItemStatus.Dismissed,
                dismissReason: reason || undefined,
                notes: dismissNotes || undefined,
            });
        } catch {
            setError(t(TK.somethingWentWrong));
            return;
        }

        setDismissTask(null);
        setDismissReason(TaskDismissReasons.notApplicable);
        setDismissOtherReason('');
        setDismissNotes('');
        await loadTasks();
    };

    const startRowEdit = (task: TaskItemData) => {
        setRowEdit({
            taskId: task.id,
            clientId: task.clientId,
            assignedUserId: task.assignedUserId,
            title: task.title || '',
            dueDate: moment(task.dueDate).format('YYYY-MM-DDTHH:mm'),
        });
    };

    const saveRowEdit = React.useCallback(async (task: TaskItemData, editState: TaskRowEditState) => {
        const normalizedAssignedUserId = editState.assignedUserId || undefined;
        const normalizedClientId = editState.clientId || undefined;
        const normalizedTitle = editState.title.trim() || undefined;
        const nextDueDateStr = editState.dueDate || task.dueDate;
        if (!moment(nextDueDateStr).isValid()) {
            setError(t(TK.somethingWentWrong));
            return;
        }

        const isAssignmentChanged = normalizedAssignedUserId !== (task.assignedUserId || undefined);
        const isDetailsChanged = normalizedClientId !== (task.clientId || undefined)
            || normalizedTitle !== (task.title || undefined)
            || moment(nextDueDateStr).valueOf() !== moment(task.dueDate).valueOf();

        if (!isAssignmentChanged && !isDetailsChanged) {
            setRowEdit(prev => prev?.taskId === editState.taskId ? null : prev);
            return;
        }

        setSavingRow(true);
        try {
            if (isDetailsChanged) {
                const payload: UpdateTaskItemData = {
                    clientId: normalizedClientId,
                    title: normalizedTitle,
                    dueDate: nextDueDateStr,
                };
                await updateTaskAsync(task.id, payload);
            }

            if (isAssignmentChanged) {
                const assignPayload: AssignTaskItemData = { assignedUserId: normalizedAssignedUserId };
                await assignTaskAsync(task.id, assignPayload);
            }

            await loadTasks();
            setRowEdit(prev => prev?.taskId === editState.taskId ? null : prev);
        } finally {
            setSavingRow(false);
        }
    }, [loadTasks, t]);

    const handleSaveRowEdit = React.useCallback(async (task: TaskItemData) => {
        if (!rowEdit || rowEdit.taskId !== task.id) return;
        await saveRowEdit(task, rowEdit);
    }, [rowEdit, saveRowEdit]);

    React.useEffect(() => {
        if (!rowEdit || isSavingRow) {
            return;
        }

        const onDocumentMouseDown = (event: MouseEvent) => {
            if (editingRowRef.current?.contains(event.target as Node)) {
                return;
            }

            const editingTask = tasks.find(task => task.id === rowEdit.taskId);
            if (!editingTask) {
                setRowEdit(prev => prev?.taskId === rowEdit.taskId ? null : prev);
                return;
            }

            void saveRowEdit(editingTask, rowEdit);
        };

        document.addEventListener('mousedown', onDocumentMouseDown);
        return () => document.removeEventListener('mousedown', onDocumentMouseDown);
    }, [isSavingRow, rowEdit, saveRowEdit, tasks]);

    return (
        <Card>
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 style={{ margin: 0 }}>{t(TK.tasks)}</h4>
                    {canCreateTaskItems && (
                        <AppButton color="success" onClick={() => setShowCreate(true)}>{t(TK.createTask)}</AppButton>
                    )}
                </div>

                <div className="d-flex flex-wrap" style={{ gap: 'var(--spacer-S)', marginBottom: 'var(--spacer-S)' }}>
                    <FormGroup style={{ minWidth: 180 }}>
                        <Label>{t(TK.status)}</Label>
                        <Input
                            type="select"
                            value={filters.status ?? ''}
                            onChange={event => setFilters(prev => ({
                                ...prev,
                                status: event.target.value === '' ? undefined : Number(event.target.value) as TaskItemStatus,
                            }))}
                        >
                            <option value="">{t(TK.all)}</option>
                            <option value={TaskItemStatus.Pending}>{t(TK.taskStatusPending)}</option>
                            <option value={TaskItemStatus.Completed}>{t(TK.taskStatusCompleted)}</option>
                            <option value={TaskItemStatus.Dismissed}>{t(TK.taskStatusDismissed)}</option>
                        </Input>
                    </FormGroup>

                    {hasScopedManagementAccess && users.length > 1 && (
                        <FormGroup style={{ minWidth: 220 }}>
                            <Label>{t(TK.responsible)}</Label>
                            <Input
                                type="select"
                                value={filters.assignedUserId ?? ''}
                                onChange={event => setFilters(prev => ({
                                    ...prev,
                                    assignedUserId: event.target.value || undefined,
                                }))}
                            >
                                <option value="">{t(TK.all)}</option>
                                {users.map(u => <option key={u.id} value={u.id}>{u.name || u.username}</option>)}
                            </Input>
                        </FormGroup>
                    )}

                    <FormGroup>
                        <Label>{t(TK.start)}</Label>
                        <Input
                            type="date"
                            value={filters.dateFrom ?? ''}
                            onChange={event => setFilters(prev => ({ ...prev, dateFrom: event.target.value || undefined }))}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>{t(TK.end)}</Label>
                        <Input
                            type="date"
                            value={filters.dateTo ?? ''}
                            onChange={event => setFilters(prev => ({ ...prev, dateTo: event.target.value || undefined }))}
                        />
                    </FormGroup>
                </div>

                {error && <Alert color="danger">{error}</Alert>}

                <TaskItemsTable
                    tasks={tasks}
                    user={user}
                    canManageVisibleTasks={hasScopedManagementAccess}
                    isLoading={isLoading}
                    emptyStateLabel={t(TK.noTasksFound)}
                    clients={clients}
                    users={users}
                    rowEdit={rowEdit}
                    setRowEdit={setRowEdit}
                    isSavingRow={isSavingRow}
                    editingRowRef={editingRowRef}
                    allowInlineEdit
                    onStartEdit={startRowEdit}
                    onSaveEdit={handleSaveRowEdit}
                    onCancelEdit={() => setRowEdit(null)}
                    onCompleteTask={setCompleteTask}
                    onDismissTask={setDismissTask}
                    onNavigateClient={navigateToClient}
                    onNavigateProfessional={navigateToProfessional}
                />

                {dismissTask && (
                    <div className="mt-3 p-3" style={{ border: '1px solid var(--surface-border-subtle)', borderRadius: 8 }}>
                        <h6>{t(TK.dismissTask)}</h6>
                        <FormGroup>
                            <Label>{t(TK.dismissReason)}</Label>
                            <Input type="select" value={dismissReason} onChange={event => setDismissReason(event.target.value)}>
                                {dismissReasonOptions.map(option => <option key={option.value} value={option.value}>{t(option.labelKey)}</option>)}
                            </Input>
                        </FormGroup>
                        {dismissReason === TaskDismissReasons.other && (
                            <FormGroup>
                                <Label>{t(TK.reason)}</Label>
                                <Input value={dismissOtherReason} onChange={event => setDismissOtherReason(event.target.value)} />
                            </FormGroup>
                        )}
                        <FormGroup>
                            <Label>{t(TK.notes)}</Label>
                            <Input type="textarea" value={dismissNotes} onChange={event => setDismissNotes(event.target.value)} />
                        </FormGroup>
                        <div className="d-flex justify-content-end" style={{ gap: 'var(--spacer-XS)' }}>
                            <AppButton onClick={() => setDismissTask(null)}>{t(TK.cancel)}</AppButton>
                            <AppButton color="danger" onClick={() => handleDismissTask(dismissTask)}>
                                {t(TK.dismissTask)}
                            </AppButton>
                        </div>
                    </div>
                )}
            </CardBody>

            <TaskCreateModal
                show={showCreate}
                onClose={() => setShowCreate(false)}
                initialAssignedUserId={!hasScopedManagementAccess ? user?.id : undefined}
                allowAssignedUserSelection={hasScopedManagementAccess && users.length > 1}
                users={users}
                clients={clients}
            />
            <TaskCompleteDialog
                task={completeTask}
                onClose={() => setCompleteTask(null)}
                onCompleted={loadTasks}
            />
        </Card>
    );
};

export default TasksPage;
